import { useEffect, useMemo, useState } from "react";
import { saveAs } from "file-saver";
import { showMessage } from "../../Functionalities/Functions";
import confirmBox from "../../CoreComponents/confirmBox";
import { fmtDateWithWeekday } from "../Insights/format";
import { money } from "./settlementParts";
import {
  entryFor,
  fetchRiderSettlement,
  loadSlabs,
  riderCsv,
  saveSlabs,
  settlementOf,
  submitRiderSalary,
  withSubmittedStatement,
} from "./riderLogic";

export default function useSalarySheet(initialDate) {
  const [date, setDate] = useState(initialDate);
  const [sheet, setSheet] = useState(null);
  const [entries, setEntries] = useState({});
  const [slabs, setSlabs] = useState(() => loadSlabs(window.localStorage));
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [submitting, setSubmitting] = useState(null);
  const rows = sheet ? sheet.riders : [];

  useEffect(() => saveSlabs(window.localStorage, slabs), [slabs]);

  const entryOf = (rider) => entries[rider.id] || entryFor(rider, slabs);
  const change = (rider, field, value) => setEntries((prev) => ({ ...prev, [rider.id]: { ...entryOf(rider), [field]: value } }));

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((item) => String(item.id).includes(query) || String(item.name || "").toLowerCase().includes(query));
  }, [rows, search]);

  const load = async () => {
    setBusy("load");
    const body = await fetchRiderSettlement(date);
    setBusy("");
    if (body && body.Status === true) {
      setSheet({ ...body, date });
      setEntries({});
      showMessage(`${body.totals.orders} completed orders by ${body.totals.riders} riders on ${date}`, "info", "Loaded", "top", "top-right");
    } else {
      setSheet(null);
      showMessage((body && body.Error) || "Could not load the settlement", "danger", "Error!", "top", "top-right");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitConfirmed = async (rider, entry) => {
    setSubmitting(rider.id);
    const body = await submitRiderSalary(sheet.date, rider.id, entry);
    setSubmitting(null);
    if (body && body.Status === true) {
      setSheet((prev) => prev && withSubmittedStatement(prev, rider.id, body.statement));
      showMessage(`${rider.name}: salary ${money(body.statement.salary)} submitted`, "success", "Submitted", "top", "top-right");
    } else {
      showMessage((body && body.Error) || "Could not submit the salary", "danger", "Error!", "top", "top-right");
    }
  };

  const submit = (rider) => {
    const entry = entryOf(rider);
    const s = settlementOf(rider, entry);
    confirmBox({
      title: `Submit ${rider.name || `Rider #${rider.id}`}'s salary?`,
      message: `${money(s.salary)} for ${fmtDateWithWeekday(sheet.date)}. The rider is told at once.`,
      yesLabel: "Submit salary",
      noLabel: "Cancel",
      onYes: () => submitConfirmed(rider, entry),
    });
  };

  const download = () => {
    saveAs(new Blob([riderCsv(rows, entries, slabs)], { type: "text/csv;charset=utf-8" }), `rider-settlement-${sheet.date}.csv`);
  };

  return { date, setDate, sheet, rows, visible, slabs, setSlabs, search, setSearch, busy, submitting, entryOf, change, load, submit, download };
}
